import type { MDXMetadata } from 'mdx/types'
import { Link, useLocation } from '#router'
import { countUnitsAndLessons } from '@/lib/seo-config'
import CourseContent from './course-content'
import Footer from './footer'

function CourseCard({ metadata }: { metadata: MDXMetadata }) {
  const { units, lessons } = countUnitsAndLessons(metadata)
  return (
    <li>
      <Link
        className="bg-card text-card-foreground flex h-full flex-col rounded-xl border p-6"
        href={metadata.href}
      >
        <span className="mb-6 ml-3">{metadata.icon}</span>
        <div className="mb-2 font-semibold">{metadata.title}</div>
        <div className="text-muted-foreground mb-4 flex-1">
          {metadata.subtitle}
        </div>
        <div className="text-muted-foreground text-sm">
          {units > 0 && (
            <>
              {units} {units === 1 ? 'unit' : 'units'}
            </>
          )}
          {units > 0 && lessons > 0 && ' · '}
          {lessons > 0 && (
            <>
              {lessons} {lessons === 1 ? 'lesson' : 'lessons'}
            </>
          )}
        </div>
      </Link>
    </li>
  )
}

function CourseChildren({ metadata: n0 }: { metadata: MDXMetadata }) {
  if (!n0.children?.length) return null

  if (!n0.children.every((child) => child.type === 'collection')) {
    return (
      <ul className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {n0.children.map((child) => (
          <CourseCard key={child.href} metadata={child} />
        ))}
      </ul>
    )
  }

  return (
    <div>
      {n0.children.map((n1) => {
        if (!n1.children?.length) return null
        if (!n1.children.every((child) => child.type === 'collection')) {
          return (
            <section key={n1.href} className="mb-16">
              <h2 className="font-semibold">{n1.title}</h2>
              <p className="text-muted-foreground text-sm">{n1.subtitle}</p>
              <ul className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {n1.children.map((child) => (
                  <CourseCard key={child.href} metadata={child} />
                ))}
              </ul>
            </section>
          )
        }

        return (
          <section key={n1.href} className="mb-16">
            <h2 className="font-semibold">{n1.title}</h2>
            <p className="text-muted-foreground mb-6 text-sm">{n1.subtitle}</p>

            {n1.children.map((n2) => {
              if (!n2.children?.length) return null

              return (
                <section key={n2.href} className="mt-6">
                  <h3 className="text-muted-foreground text-sm tracking-wide">
                    {n2.title}
                  </h3>
                  <ul className="mt-2 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {n2.children.map((child) => (
                      <CourseCard key={child.href} metadata={child} />
                    ))}
                  </ul>
                </section>
              )
            })}
          </section>
        )
      })}
    </div>
  )
}

export default function CourseList() {
  const { page } = useLocation()
  if (!page) return null

  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-col">
      <div className="mx-auto my-10 max-w-screen-lg px-8">
        <CourseContent />
        <CourseChildren metadata={page} />
      </div>
      <Footer />
    </div>
  )
}
